"use client";

import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { parseHrtIds } from "./parseHrtIds";

export { parseHrtIds };

/**
 * Reads and validates `hrtId` from the URL, redirecting to `/{clientSlug}` when
 * it is genuinely missing/malformed.
 *
 * The redirect lives here (client-side), NOT in the schedule/review server
 * components: Next prefetches routes by pathname only (searchParams dropped),
 * so a server-side `redirect()` gated on `hrtId` fires on the hrtId-less
 * prefetch and bounces the real navigation. It also does NOT trust the first
 * `useSearchParams()` read — that lags a render or two after a fast client nav,
 * which is what made the old redirect flaky. Instead it defers briefly and
 * re-checks the live URL (`window.location`, authoritative, never lagged), so
 * a legitimately-present hrtId can't be misread as missing.
 */
export function useValidatedHrtIds() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { clientSlug } = useParams<{ clientSlug: string }>();

  const initialHrtIds = parseHrtIds(searchParams.get("hrtId"));
  const hasValidHrtIds = initialHrtIds !== undefined;
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current || hasValidHrtIds) return;
    const timer = setTimeout(() => {
      if (hasFiredRef.current) return;
      // Authoritative re-check against the live URL — if hrtId is actually
      // present, useSearchParams just hasn't caught up; don't bounce.
      const liveHrtId = new URLSearchParams(window.location.search).get(
        "hrtId",
      );
      if (parseHrtIds(liveHrtId) !== undefined) return;
      hasFiredRef.current = true;
      router.replace(`/${clientSlug}`);
    }, 300);
    return () => clearTimeout(timer);
  }, [hasValidHrtIds, clientSlug, router]);

  return { initialHrtIds, hasValidHrtIds };
}
